import React from "react";
import ButtonStrip from "../../common/ButtonStrip";
import LineHeightLarge from "./img/LineHeightLarge";
import LineHeightMedium from "./img/LineHeightMedium";
import LineHeightSmall from "./img/LineHeightSmall";
import MarginLarge from "./img/PageMarginLarge";
import MarginMedium from "./img/PageMarginMedium";
import MarginSmall from "./img/PageMarginSmall";
import PopoverHeader from "../../common/PopoverHeader";
import styled, { css } from "styled-components";
import withFormatting, { FormattingProps } from "./withFormatting";
import { FormatOptions } from "./FormatOptions";
import { FormattedMessage, useIntl } from "react-intl";
import { Select, Button } from "@vitalsource/vstui";
import { ViewerProps } from "../../viewer-context/withViewer";
import { cloud, ninja } from "@vitalsource/vstui/colors";
import { createThemedComponent } from "@vitalsource/vstui/themes";
import useBrandProp from "components/brand-context/useBrandProp";

const SECTION_PADDING = `12px 16px`;

const modeButton = (bgColor: string, color: string) => {
  return createThemedComponent(Button, {
    Button_borderColor_themePrimary: cloud.getShade(4),
    Button_borderColor_themePrimary_focus: cloud.getShade(4),
    Button_borderColor_themePrimary_hover: cloud.getShade(4),

    Button_backgroundColor_themePrimary: bgColor,
    Button_backgroundColor_themePrimary_active: bgColor,
    Button_backgroundColor_themePrimary_focus: bgColor,
    Button_backgroundColor_themePrimary_hover: bgColor,

    Button_color_themePrimary: color,
    Button_color_themePrimary_focus: color,
    Button_color_themePrimary_hover: color,
    Button_color_themePrimary_active: color,

    Button_paddingHorizontal_medium: "15px",
  });
};

const DayButton = modeButton("#FFF", cloud.getShade(9));
const NightButton = modeButton(ninja.getShade(9), "#FFF");
const SepiaButton = modeButton("#e7dcd1", "#493420");
const CyanButton = modeButton("#d8e8f0", "#3c3c3c");

const CustomButton = () => {
  const color = (props: any) => props.theme.vstui.color.primary.getShade(7);

  return createThemedComponent(Button, {
    Button_borderColor_themePrimaryNaked_focus: cloud.getShade(4),
    Button_boxShadow_themePrimaryNaked_focus: `0 0 0 2px #fff, 0 0 0 4px ${color}`,
    Button_borderColor_themePrimaryNaked: cloud.getShade(4),
    Button_borderColor_themePrimaryNaked_hover: cloud.getShade(4),
  });
};
const CustomFocus = CustomButton();

const controls = {
  fonts: [
    { key: "default", value: "default" },
    { key: "sansSerif", value: "--RS__sansTf" },
    { key: "oldStyle", value: "--RS__oldStyleTf" },
    { key: "modern", value: "--RS__modernTf" },
    { key: "humanist", value: "--RS__humanistTf" },
    { key: "monospace", value: "--RS__monospaceTf" },
    { key: "openDyslexic", value: "--RS__openDyslexicTf" },
  ],
  lineHeightSizes: [
    { key: "small", value: 1, img: LineHeightSmall },
    { key: "medium", value: 1.5, img: LineHeightMedium },
    { key: "large", value: 2, img: LineHeightLarge },
  ],
  marginSizes: [
    { key: "small", value: 1, img: MarginSmall },
    { key: "medium", value: 2.5, img: MarginMedium },
    { key: "large", value: 5, img: MarginLarge },
  ],
  modes: [
    { key: "day", button: DayButton },
    { key: "night", button: NightButton },
    { key: "sepia", button: SepiaButton },
    { key: "cyan", button: CyanButton },
  ],
  fontSizes: [{ size: 1 }, { size: 1.5 }, { size: 2 }, { size: 4 }],
};

const blueOutlineStyles = css`
  outline: none;
  border-radius: 4px;
  ${(p: any) =>
    p.theme &&
    `box-shadow: 0 0 0 2px #fff, 0 0 0 4px ${p.theme.vstui.color.primary.getShade(
      6
    )}`}
`;

const Styled = {
  LargeIconContainer: styled.div<{ active: boolean }>`
    button:focus {
      ${blueOutlineStyles}
    }
    ${p => p.active && blueOutlineStyles}
  `,
  SelectedContainer: styled.div<{ active: boolean; border?: boolean }>`
    ${p => p.border && blueOutlineStyles}
    ${p => p.active && blueOutlineStyles}
  `,
  Footer: styled.div`
    font-size: 1.4rem;
    font-weight: 400;
    text-align: center;
    border-top: 1px solid ${cloud.getShade(3)};
    padding: 8px 0 15px 0;
  `,
  MenuSection: styled.div`
    padding: ${SECTION_PADDING};
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  `,
  MenuSectionNoSpace: styled.div`
    padding: ${SECTION_PADDING};
    display: flex;
    flex-direction: row;
    justify-content: center;
  `,
  MenuSectionNoPad: styled.div`
    padding: ${SECTION_PADDING} 0 16px;
    display: flex;
    flex-direction: row;
    justify-content: center;
    div:first-of-type {
      margin-bottom: 12px;
    }
  `,
  MenuContainer: styled.div`
    width: 320px;
  `,
  ResetLink: styled.div`
    font-size: 1.2rem;
    padding-top: 2px;
  `,
};

const EnhancedFormatting = (props: FormattingProps & ViewerProps) => {
  const intl = useIntl();
  const marginEnabled = useBrandProp("ContentControls.margin.enabled");

  const fontStyleOptions = () => {
    return (
      <Select
        label={intl.formatMessage({ id: "format.fontStyle" })}
        labelHidden
        onChange={event => {
          props.setReaderOptions(FormatOptions.FONT, event.target.value);
        }}
        fullWidth
        value={props.readerOptions.font}
      >
        {controls.fonts.map(fontItem => (
          <option value={fontItem.value} key={fontItem.key}>
            {intl.formatMessage({ id: `format.font.${fontItem.key}` })}
          </option>
        ))}
      </Select>
    );
  };

  const modeOptions = () => {
    return controls.modes.map(mode => {
      return (
        <Styled.SelectedContainer
          key={mode.key}
          active={props.readerOptions.mode === mode.key}
        >
          <mode.button
            onClick={() => props.setReaderOptions(FormatOptions.MODE, mode.key)}
          >
            {intl.formatMessage({ id: `format.mode.${mode.key}` })}
          </mode.button>
        </Styled.SelectedContainer>
      );
    });
  };

  const marginOptions = () => {
    if (marginEnabled) {
      return (
        <>
          <PopoverHeader>
            <FormattedMessage id="format.margin" />
          </PopoverHeader>
          <Styled.MenuSection>
            {controls.marginSizes.map(margin => (
              <Styled.LargeIconContainer
                key={margin.key}
                active={props.readerOptions.margin === margin.value}
              >
                <CustomFocus
                  naked
                  inline
                  label={intl.formatMessage({
                    id: `format.margin.${margin.key}`,
                  })}
                  onClick={() =>
                    props.setReaderOptions(FormatOptions.MARGIN, margin.value)
                  }
                >
                  <margin.img />
                </CustomFocus>
              </Styled.LargeIconContainer>
            ))}
          </Styled.MenuSection>
        </>
      );
    }
  };

  const lineHeightOptions = () => {
    return controls.lineHeightSizes.map(lineHeight => (
      <Styled.LargeIconContainer
        key={lineHeight.key}
        active={props.readerOptions.lineHeight === lineHeight.value}
      >
        <CustomFocus
          naked
          inline
          label={intl.formatMessage({
            id: `format.lineHeight.${lineHeight.key}`,
          })}
          onClick={() =>
            props.setReaderOptions(FormatOptions.LINEHEIGHT, lineHeight.value)
          }
        >
          <lineHeight.img />
        </CustomFocus>
      </Styled.LargeIconContainer>
    ));
  };

  return (
    <Styled.MenuContainer data-testid="enhanced_menu">
      <PopoverHeader>
        <FormattedMessage id="format.textSize" />
      </PopoverHeader>
      <Styled.MenuSectionNoSpace>
        <ButtonStrip
          selectedButton={controls.fontSizes.findIndex(
            x => x.size === props.readerOptions.text
          )}
          buttons={[
            {
              label: intl.formatMessage({ id: "format.textSize.small" }),
              content: <div style={{ fontSize: 12 }}>Aa</div>,
              onClick: () => props.setReaderOptions(FormatOptions.TEXT, 1),
            },
            {
              label: intl.formatMessage({ id: "format.textSize.normal" }),
              content: <div style={{ fontSize: 16 }}>Aa</div>,
              onClick: () => props.setReaderOptions(FormatOptions.TEXT, 1.5),
            },
            {
              label: intl.formatMessage({ id: "format.textSize.large" }),
              content: <div style={{ fontSize: 19 }}>Aa</div>,
              onClick: () => props.setReaderOptions(FormatOptions.TEXT, 2),
            },
            {
              label: intl.formatMessage({
                id: "format.textSize.extraLarge",
              }),
              content: <div style={{ fontSize: 22 }}>Aa</div>,
              onClick: () => props.setReaderOptions(FormatOptions.TEXT, 4),
            },
          ]}
        />
      </Styled.MenuSectionNoSpace>
      <PopoverHeader>
        <FormattedMessage id="format.font" />
      </PopoverHeader>
      <Styled.MenuSectionNoPad>{fontStyleOptions()}</Styled.MenuSectionNoPad>
      <PopoverHeader>
        <FormattedMessage id="format.mode" />
      </PopoverHeader>
      <Styled.MenuSection>{modeOptions()}</Styled.MenuSection>
      {marginOptions()}
      <PopoverHeader>
        <FormattedMessage id="format.lineHeight" />
      </PopoverHeader>
      <Styled.MenuSection>{lineHeightOptions()}</Styled.MenuSection>
      <Styled.Footer>
        <FormattedMessage id="format.lookRight" />
        <Styled.ResetLink>
          <Button inline onClick={props.resetReader}>
            <FormattedMessage id="format.reset" />
          </Button>
        </Styled.ResetLink>
      </Styled.Footer>
    </Styled.MenuContainer>
  );
};

export default withFormatting(EnhancedFormatting);
